import { unreachable } from 'module/helpers/unreachable';
import { useTranslate, UseTranslateType } from 'module/hooks/use-translation';
import {
  TableCellType,
  TableDataType,
} from 'module/ui/components/atoms/rich-text-editor/toolbar/table-toolbar';
import {
  faBriefcaseBlank,
  faEnvelope,
  faHandshake,
  faListCheck,
  faMagnifyingGlass,
  faQuestionCircle,
  faSignal,
  faDatabase,
  faTable,
} from '@fortawesome/pro-regular-svg-icons';
import { Editor, Range } from '@tiptap/core';
import {
  CustomSectionState,
  CustomSectionType,
  CustomSectionContentState,
} from 'api/custom-section/types';
import { CustomGroupSectionType } from 'api/custom-section/types/group-section';
import { PageType } from 'api/page-template/types';
import { startCase } from 'lodash';
import { ReactNode, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { CommandPickerItem, CommandPickerLeafItem } from '../types';
import { CommandListItemIcon } from './common';
import { isAllowedOnlyAtFirstLevel } from './utils';
import { defaultGroupSectionConfig } from '../../custom-group-section/default-config';
import { defaultCustomSectionNodeConfig } from '../../custom-section/utils';

enum AiFillSectionCommand {
  Question = 'question',
  Action = 'action',
  ExtractInfo = 'extract-info',
  Signal = 'signal',
  MeetingDetails = 'meeting-details',
  AccountDetails = 'account-details',
  EmailDetails = 'email-details',
  TableDetails = 'table-details',
  MultiQuestion = 'multi-question',
  MultiQuestions = 'multi-questions',
  RecordBlock = 'record-block',
}
const sectionCommands = [
  AiFillSectionCommand.Question,
  AiFillSectionCommand.Action,
  AiFillSectionCommand.ExtractInfo,
  // AiFillSectionCommand.Signal,
  AiFillSectionCommand.TableDetails,
  // AiFillSectionCommand.MultiQuestion,
  AiFillSectionCommand.MultiQuestions,
  AiFillSectionCommand.RecordBlock,
];
const blockCommands = [
  AiFillSectionCommand.MeetingDetails,
  AiFillSectionCommand.AccountDetails,
];

const emailBlockCommands = [
  AiFillSectionCommand.EmailDetails,
  AiFillSectionCommand.AccountDetails,
];
const getAttributesForSectionType = (sectionType: CustomSectionType) => {
  const baseAttributes = {
    contentState: CustomSectionContentState.Prompt,
    state: CustomSectionState.Open,
  };

  switch (sectionType) {
    case CustomSectionType.Question:
    case CustomSectionType.Action:
    case CustomSectionType.ExtractInfo:
    case CustomSectionType.SignalDetails:
    case CustomSectionType.MeetingDetails:
    case CustomSectionType.AccountDetails:
    case CustomSectionType.EmailDetails:
    case CustomSectionType.CompanyPageSourceDerivedSection:
    case CustomSectionType.CustomTextSection:
    case CustomSectionType.PeoplePageSourceDerivedQuestionSection:
    case CustomSectionType.PeoplePageSourceDerivedSignalSection:
    case CustomSectionType.MultiQuestion:
    case CustomSectionType.QuestionAnswerItem:
    case CustomSectionType.TableDetails:
    case CustomSectionType.RecordBlock:
      return {
        ...defaultCustomSectionNodeConfig[sectionType],
        ...baseAttributes,
      };
    default:
      return unreachable(sectionType);
  }
};
const insertSection =
  (sectionType: CustomSectionType, content?: any) =>
  (editor: Editor, range: Range) => {
    return editor
      .chain()
      .focus()
      .addCustomSection({
        insertAt: range.from,
        attributes: getAttributesForSectionType(sectionType),
        content: content,
      })
      .run();
  };

const insertGroupSection =
  (sectionType: CustomGroupSectionType) => (editor: Editor, range: Range) => {
    return editor
      .chain()
      .focus()
      .addCustomGroupSection({
        insertAt: range.from,
        attributes: defaultGroupSectionConfig[sectionType],
      })
      .run();
  };

const aiFillSectionCommandMap: Record<
  AiFillSectionCommand,
  {
    icon: ReactNode;
    execute: (editor: Editor, range: Range) => void;
    isAllowed?: (editor: Editor, range: Range) => boolean;
  }
> = {
  [AiFillSectionCommand.Question]: {
    icon: <CommandListItemIcon icon={faQuestionCircle} />,
    execute: insertSection(CustomSectionType.Question),
    isAllowed: isAllowedOnlyAtFirstLevel,
  },
  [AiFillSectionCommand.Action]: {
    icon: <CommandListItemIcon icon={faListCheck} />,
    execute: insertSection(CustomSectionType.Action),
    isAllowed: isAllowedOnlyAtFirstLevel,
  },
  [AiFillSectionCommand.ExtractInfo]: {
    icon: <CommandListItemIcon icon={faMagnifyingGlass} />,
    execute: insertSection(CustomSectionType.ExtractInfo),
    isAllowed: isAllowedOnlyAtFirstLevel,
  },
  [AiFillSectionCommand.Signal]: {
    icon: <CommandListItemIcon icon={faSignal} />,
    execute: insertSection(CustomSectionType.SignalDetails),
    isAllowed: isAllowedOnlyAtFirstLevel,
  },
  [AiFillSectionCommand.MeetingDetails]: {
    icon: <CommandListItemIcon icon={faHandshake} />,
    execute: insertSection(CustomSectionType.MeetingDetails),
    isAllowed: isAllowedOnlyAtFirstLevel,
  },
  [AiFillSectionCommand.AccountDetails]: {
    icon: <CommandListItemIcon icon={faBriefcaseBlank} />,
    execute: insertSection(CustomSectionType.AccountDetails),
    isAllowed: isAllowedOnlyAtFirstLevel,
  },
  [AiFillSectionCommand.EmailDetails]: {
    icon: <CommandListItemIcon icon={faEnvelope} />,
    execute: insertSection(CustomSectionType.EmailDetails),
    isAllowed: isAllowedOnlyAtFirstLevel,
  },
  [AiFillSectionCommand.TableDetails]: {
    icon: <CommandListItemIcon icon={faTable} />,
    execute: insertSection(CustomSectionType.TableDetails, [
      {
        type: 'table',
        content: [
          {
            type: 'tableRow',
            content: [
              {
                type: 'tableHeader',
                attrs: {
                  colspan: 1,
                  config: {
                    id: uuidv4(),
                    name: 'Column',
                    dataType: TableDataType.TEXT,
                    aiContext: '',
                    cellType: TableCellType.COLUMN,
                  },
                },
                content: [
                  {
                    type: 'tableHeaderLeafNode',
                    content: [
                      {
                        type: 'paragraph',
                        content: [{ type: 'text', text: 'Column' }],
                      },
                    ],
                  },
                ],
              },
              {
                type: 'tableHeader',
                attrs: {
                  colspan: 1,
                  config: {
                    id: uuidv4(),
                    name: 'Column',
                    dataType: TableDataType.TEXT,
                    aiContext: '',
                    cellType: TableCellType.COLUMN,
                  },
                },
                content: [
                  {
                    type: 'tableHeaderLeafNode',
                    content: [
                      {
                        type: 'paragraph',
                        content: [{ type: 'text', text: 'Column' }],
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            type: 'tableRow',
            content: [
              {
                type: 'tableHeader',
                attrs: {
                  colspan: 1,
                  config: {
                    id: uuidv4(),
                    name: 'Row',
                    dataType: TableDataType.TEXT,
                    aiContext: '',
                    cellType: TableCellType.ROW,
                  },
                },
                content: [
                  {
                    type: 'tableHeaderLeafNode',
                    content: [
                      {
                        type: 'paragraph',
                        content: [{ type: 'text', text: 'Row' }],
                      },
                    ],
                  },
                ],
              },
              {
                type: 'tableCell',
                content: [{ type: 'paragraph' }],
              },
            ],
          },
          {
            type: 'tableRow',
            content: [
              {
                type: 'tableHeader',
                attrs: {
                  colspan: 1,
                  config: {
                    id: uuidv4(),
                    name: 'Row',
                    dataType: TableDataType.TEXT,
                    aiContext: '',
                    cellType: TableCellType.ROW,
                  },
                },
                content: [
                  {
                    type: 'tableHeaderLeafNode',
                    content: [
                      {
                        type: 'paragraph',
                        content: [{ type: 'text', text: 'Row' }],
                      },
                    ],
                  },
                ],
              },
              {
                type: 'tableCell',
                content: [{ type: 'paragraph' }],
              },
            ],
          },
        ],
      },
    ]),
    isAllowed: isAllowedOnlyAtFirstLevel,
  },
  [AiFillSectionCommand.RecordBlock]: {
    icon: <CommandListItemIcon icon={faDatabase} />,
    execute: insertSection(CustomSectionType.RecordBlock, [
      {
        type: 'table',
        content: [
          {
            type: 'tableRow',
            content: [
              {
                type: 'tableHeader',
                attrs: {
                  colspan: 1,
                  config: {
                    id: uuidv4(),
                    name: 'Name',
                    dataType: TableDataType.TEXT,
                    aiContext: '',
                    cellType: TableCellType.COLUMN,
                  },
                },
                content: [
                  {
                    type: 'tableHeaderLeafNode',
                    content: [
                      {
                        type: 'paragraph',
                        content: [{ type: 'text', text: 'Name' }],
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            type: 'tableRow',
            content: [
              {
                type: 'tableCell',
                content: [{ type: 'paragraph' }],
              },
            ],
          },
          {
            type: 'tableRow',
            content: [
              {
                type: 'tableCell',
                content: [{ type: 'paragraph' }],
              },
            ],
          },
          {
            type: 'tableRow',
            content: [
              {
                type: 'tableCell',
                content: [{ type: 'paragraph' }],
              },
            ],
          },
        ],
      },
    ]),
    isAllowed: isAllowedOnlyAtFirstLevel,
  },
  [AiFillSectionCommand.MultiQuestion]: {
    icon: <CommandListItemIcon icon={faQuestionCircle} />,
    execute: insertSection(CustomSectionType.MultiQuestion),
    isAllowed: isAllowedOnlyAtFirstLevel,
  },
  [AiFillSectionCommand.MultiQuestions]: {
    icon: <CommandListItemIcon icon={faQuestionCircle} />,
    execute: insertGroupSection(CustomGroupSectionType.MultiQuestions),
    isAllowed: isAllowedOnlyAtFirstLevel,
  },
};
const aiFillSectionCommandAsItem = (
  command: AiFillSectionCommand,
  _translate: UseTranslateType,
): CommandPickerLeafItem => {
  return {
    id: command,
    label: startCase(command),
    icon: aiFillSectionCommandMap[command].icon,
    execute: aiFillSectionCommandMap[command].execute,
    isAllowed: aiFillSectionCommandMap[command].isAllowed,
  };
};

export const useGetDefaultAiSectionCommands = (
  pageType?: string,
): CommandPickerItem[] => {
  const translate = useTranslate();

  return useMemo(
    () => [
      {
        id: 'ai-fill-section',
        label: 'AI sections',
        items: sectionCommands
          .filter((command) => {
            // Remove signal block for email and company page types
            if (
              (pageType === PageType.Email || pageType === PageType.Company) &&
              command === AiFillSectionCommand.Signal
            ) {
              return false;
            }

            return true;
          })
          .map((command) =>
            aiFillSectionCommandAsItem(
              command as AiFillSectionCommand,
              translate,
            ),
          ),
      },
    ],
    [translate, pageType],
  );
};
export const useGetDefaultMeetingBlockCommands = (): CommandPickerItem[] => {
  const translate = useTranslate();

  return useMemo(
    () => [
      {
        id: 'meeting-block',
        label: 'Account summary',
        items: blockCommands.map((command) =>
          aiFillSectionCommandAsItem(
            command as AiFillSectionCommand,
            translate,
          ),
        ),
      },
    ],
    [translate],
  );
};

export const useGetDefaultEmailBlockCommands = (): CommandPickerItem[] => {
  const translate = useTranslate();

  return useMemo(
    () => [
      {
        id: 'meeting-block',
        label: 'Account summary',
        items: emailBlockCommands.map((command) =>
          aiFillSectionCommandAsItem(
            command as AiFillSectionCommand,
            translate,
          ),
        ),
      },
    ],
    [translate],
  );
};
